import { z } from 'zod';

/** One awarded component. Never negative: a term either contributes or it does not. */
const PointsSchema = z.number().min(0);

/**
 * Why this candidate scored what they scored — stored beside every nomination,
 * never sent to a member (P16b).
 *
 * ## Why a score is stored with its reasons at all
 *
 * `nominations.questionWeights` and `nominations.weights` are operator dials,
 * and a dial nobody can see the effect of is a dial nobody will touch. A bare
 * `score: 47.5` cannot answer *"why is this person second?"* — and it cannot
 * answer it **later** either, because by the time anyone asks, the weights may
 * have moved and the answer is no longer reproducible. The breakdown is the
 * row's own account of itself, written under the weights that were in force when
 * the batch was computed.
 *
 * ## It is not member-facing, and there is no contract that makes it so
 *
 * A member receives `{ userId, score, rank }` (`NominationSchema`). This shape is
 * the **stored** column: an operator-facing explanation, and eventually the input
 * to a staff "why this nomination" view. Nothing about a candidate is rendered
 * from it — it is numbers, not answers, and no answer **value** appears in it
 * even for the questions it names.
 *
 * ## The `null`s are load-bearing (P14a)
 *
 * Two components can be *uncomputable*, which is a different fact from *scored
 * zero*, and collapsing them is how a platform comes to report a confident
 * ranking built on a signal it never had:
 *
 * - `sharedOptions` is `null` when there is **no matchable `multi_select`
 *   question at all**. With such a question present but no overlap, it is `0`.
 * - `agePreference` is `null` when `nominations.agePreferenceQuestions` names
 *   questions that do not exist, are inactive, are not `number` questions, or
 *   are the same question twice. A member who simply left the range blank has
 *   stated *no preference*, which fits everybody — that is the full weight, not
 *   a `null`.
 *
 * `completion` and `recentActivity` have no such state: `completionPct` is
 * `notNull`, and a `lastActiveAt` of `null` is a member who has never been
 * active — a computed zero, not a missing measurement.
 */
export const NominationScoreBreakdownSchema = z
  .object({
    /**
     * Points per weighted question, keyed by the question's stable `key`.
     *
     * Every question that was **weighted and matchable** appears, including the
     * ones that scored `0` — "they answered differently" and "this question was
     * not considered" are different facts, and only listing the winners would
     * make them indistinguishable. A weighted key naming a question that is
     * absent, inactive, not matchable or `multi_select` is not here at all,
     * which is what "the weight is inert" looks like from the outside.
     */
    questions: z.record(z.string().min(1).max(64), PointsSchema),
    /** `jaccard × sharedOptionsMax`, or `null` when nothing could be compared. */
    sharedOptions: PointsSchema.nullable(),
    /** `completionPct / 100 × completion`. */
    completion: PointsSchema,
    /** The full `recentActivity` weight, or zero. */
    recentActivity: PointsSchema,
    /** The full `agePreference` weight, zero, or `null` when it could not be computed. */
    agePreference: PointsSchema.nullable(),
  })
  .strict();

export type NominationScoreBreakdown = z.infer<typeof NominationScoreBreakdownSchema>;

/**
 * The total the breakdown adds up to.
 *
 * Written once, here, because the scorer computes the total **in SQL** and the
 * stored row carries it in its own column: two independent spellings of the same
 * sum is exactly how a row comes to disagree with its own explanation. This is
 * the function a spec — and, later, a staff view — checks the stored `score`
 * against.
 *
 * An uncomputable component contributes **nothing**, which is the only reading
 * that keeps the total honest: a `null` is the absence of a measurement, and
 * substituting any number for it would be inventing one.
 */
export function totalOfBreakdown(breakdown: NominationScoreBreakdown): number {
  const questionPoints = Object.values(breakdown.questions).reduce(
    (sum, points) => sum + points,
    0,
  );

  return (
    questionPoints +
    (breakdown.sharedOptions ?? 0) +
    breakdown.completion +
    breakdown.recentActivity +
    (breakdown.agePreference ?? 0)
  );
}
